import React, { useMemo, useState } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../../common/CommonLoader/CommonLoader";
import AppButton from "../../../common/AppButton";
import {
  useCreateStudentCategory,
  useStudentCategories
} from "../../../../hooks/api/education/useAcademicStructure";
import { formatEntityStatus } from "../common/academicUtils";

const StudentCategoryList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const { data: categories = [], isPending, refetch } = useStudentCategories();
  const createCategory = useCreateStudentCategory();
  const [name, setName] = useState("");

  const sorted = useMemo(
    () => [...categories].sort((a, b) => a.name.localeCompare(b.name)),
    [categories]
  );

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t(
          "loading-student-categories",
          "Loading student categories..."
        )}
      />
    );
  }

  return (
    <Box data-testid="student-category-list-page" sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {t("student-categories-title", "Student categories")}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <TextField
          size="small"
          fullWidth
          label={t("student-category-name-label", "Category name")}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <AppButton
          disabled={!name.trim() || createCategory.isPending}
          onClick={async () => {
            await createCategory.mutateAsync({ name: name.trim() });
            setName("");
            refetch();
          }}
        >
          {t("add-category", "Add category")}
        </AppButton>
      </Stack>

      {sorted.length === 0 ? (
        <Typography color="text.secondary">
          {t("student-categories-empty", "No student categories yet.")}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {sorted.map((category) => (
            <Box
              component="li"
              key={category.id}
              sx={{
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between"
              }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {category.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatEntityStatus(category.status)}
                </Typography>
              </Box>
              <AppButton variant="text" onClick={() => navigate(category.id)}>
                {t("view", "View")}
              </AppButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default StudentCategoryList;
